<script setup>
import { ref, watch, onMounted } from 'vue'
import { marked } from 'marked'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const markdownContent = ref('')
const isLoading = ref(false)

const loadMarkdownContent = async (language) => {
  isLoading.value = true
  try {
    const response = await import(`../../../../markdown/${language}/privacyPolicy.md?raw`)
    markdownContent.value = marked(response.default)
  } catch (error) {
    console.error(`Failed to load Privacy Policy for language: ${language}`, error)
    // Fallback to Japanese if other language fails
    if (language !== 'ja') {
      try {
        const fallbackResponse = await import('../../../../markdown/ja/privacyPolicy.md?raw')
        markdownContent.value = marked(fallbackResponse.default)
      } catch (fallbackError) {
        console.error('Failed to load fallback Privacy Policy:', fallbackError)
        markdownContent.value = '<p>Privacy Policy could not be loaded.</p>'
      }
    } else {
      markdownContent.value = '<p>Privacy Policy could not be loaded.</p>'
    }
  } finally {
    isLoading.value = false
  }
}

// Load markdown on mount
onMounted(async () => {
  const language = locale.value || 'ja' // Default to Japanese
  await loadMarkdownContent(language)
})

// Watch for locale changes and reload markdown
watch(locale, async (newLocale) => {
  const language = newLocale || 'ja'
  await loadMarkdownContent(language)
})
</script>

<template>
  <div class="privacy-policy">
    <div v-if="isLoading" class="loading-state">
      Loading Privacy Policy...
    </div>
    <div v-else-if="markdownContent" v-html="markdownContent" class="markdown-content"></div>
    <div v-else class="error-state">
      Privacy Policy could not be loaded.
    </div>
  </div>
</template>

<style scoped>
.markdown-content {
  line-height: 1.6;
  color: #333;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.markdown-content h1 {
  font-size: 2em;
  margin-bottom: 0.5em;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.3em;
}

.markdown-content h2 {
  font-size: 1.5em;
  margin-top: 2em;
  margin-bottom: 1em;
  color: #2c3e50;
}

.markdown-content h3 {
  font-size: 1.2em;
  margin-top: 1.5em;
  margin-bottom: 0.8em;
  color: #34495e;
}

.markdown-content p {
  margin-bottom: 1em;
}

.markdown-content ul, .markdown-content ol {
  margin-bottom: 1em;
  padding-left: 2em;
}

.markdown-content li {
  margin-bottom: 0.5em;
}

.markdown-content strong {
  font-weight: 600;
}

.markdown-content em {
  font-style: italic;
}

.markdown-content code {
  background-color: #f8f9fa;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

.markdown-content blockquote {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding-left: 1em;
  color: #666;
  font-style: italic;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 1.1em;
}

.error-state {
  text-align: center;
  padding: 40px 20px;
  color: #d32f2f;
  font-size: 1.1em;
  background-color: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  margin: 20px;
}
</style>